/**
 * EU AI Act Annex III Recruitment Screening Compliance Audit Logger
 *
 * Implements mandatory event record-keeping, traceability, and audit logging
 * for high-risk AI recruitment and candidate selection screening systems
 * pursuant to EU AI Act (Regulation 2024/1689 Annex III Section 4).
 */

import { logInfo } from "./logger";

export const ANNEX_III_AUDIT_EVENTS = {
  CANDIDATE_SCREENING_EVALUATED: "annex_iii_candidate_screening_evaluated",
  TRANSPARENCY_DISCLOSURE_VIEWED: "annex_iii_transparency_disclosure_viewed",
  HUMAN_REVIEW_REQUESTED: "annex_iii_human_review_requested",
  HUMAN_DECISION_RECORDED: "annex_iii_human_decision_recorded",
  EXPLANATION_DISCLOSED: "annex_iii_explanation_disclosed",
} as const;

export type AnnexIIIAuditEventType =
  (typeof ANNEX_III_AUDIT_EVENTS)[keyof typeof ANNEX_III_AUDIT_EVENTS];

export interface AnnexIIIAuditPayload {
  candidateId?: string;
  jobId?: string;
  eventType: AnnexIIIAuditEventType;
  screeningScore?: number;
  modelId?: string;
  systemVersion?: string;
  humanReviewerId?: string;
  isHumanReviewed?: boolean;
  humanDecision?: "approved" | "rejected" | "modified" | "pending";
  disclosuresConfirmed?: boolean;
  metadata?: Record<string, unknown>;
}

export interface AnnexIIIAuditRecord extends AnnexIIIAuditPayload {
  id: string;
  timestamp: string;
  complianceScope: "EU_AI_ACT_ANNEX_III_RECRUITMENT";
  legalBasis: "EU AI Act 2024/1689 Annex III Sec. 4(a)/(b) & Art. 12 Traceability";
}

// In-memory audit log storage (fallback for client-side auditing & local inspection)
const memoryAuditLog: AnnexIIIAuditRecord[] = [];

/**
 * Creates a validated Annex III compliance audit record.
 */
export function createAnnexIIIAuditRecord(
  payload: AnnexIIIAuditPayload
): AnnexIIIAuditRecord {
  const recordId = `annex3-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  const record: AnnexIIIAuditRecord = {
    id: recordId,
    timestamp: new Date().toISOString(),
    complianceScope: "EU_AI_ACT_ANNEX_III_RECRUITMENT",
    legalBasis: "EU AI Act 2024/1689 Annex III Sec. 4(a)/(b) & Art. 12 Traceability",
    modelId: payload.modelId ?? "mistral-large-latest",
    systemVersion: payload.systemVersion ?? "v1.0-annex-iii",
    isHumanReviewed: payload.isHumanReviewed ?? false,
    disclosuresConfirmed: payload.disclosuresConfirmed ?? true,
    ...payload,
  };
  return record;
}

/**
 * Logs an Annex III recruitment screening compliance audit event.
 * Outputs structured JSON logs and records to in-memory audit store.
 */
export function logAnnexIIIAuditEvent(
  payload: AnnexIIIAuditPayload
): AnnexIIIAuditRecord {
  const record = createAnnexIIIAuditRecord(payload);

  // Store locally for UI transparency views & inspection
  memoryAuditLog.unshift(record);
  if (memoryAuditLog.length > 100) {
    memoryAuditLog.pop();
  }

  // Also persist in localStorage if in browser environment
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      const existingStr = window.localStorage.getItem("alchema_annex_iii_audit_logs");
      const existing: AnnexIIIAuditRecord[] = existingStr ? JSON.parse(existingStr) : [];
      existing.unshift(record);
      if (existing.length > 50) existing.pop();
      window.localStorage.setItem("alchema_annex_iii_audit_logs", JSON.stringify(existing));
    } catch {
      // Ignore localStorage errors (e.g. quota exceeded or SSR)
    }
  }

  // Emit structured production audit log via logger
  logInfo("EU_AI_ACT_ANNEX_III_AUDIT", {
    auditRecord: record,
    complianceScope: record.complianceScope,
    candidateId: record.candidateId,
    eventType: record.eventType,
    screeningScore: record.screeningScore,
  });

  return record;
}

/**
 * Retrieves all stored Annex III recruitment compliance audit logs.
 */
export function getAnnexIIIAuditLogs(): AnnexIIIAuditRecord[] {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      const stored = window.localStorage.getItem("alchema_annex_iii_audit_logs");
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
      // Fallback to in-memory
    }
  }
  return [...memoryAuditLog];
}

/**
 * Clears in-memory audit logs (useful for testing).
 */
export function clearAnnexIIIAuditLogs(): void {
  memoryAuditLog.length = 0;
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.removeItem("alchema_annex_iii_audit_logs");
    } catch {
      // Ignore
    }
  }
}
